import { ReactElement, useState } from 'react'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import {
  CancelIcon,
  SubmitIcon,
  UploadFileIcon,
  UploadIcon,
} from 'src/v3/core/icons'
import { useAppDispatch } from 'src/store/hooks'

import {
  fetchNREMTLicense,
  fetchNREMTVerificationStatus,
} from 'src/v3/manage/manage.slice'
import { NREMTLicense } from 'src/v3/core/types'
import { FileData, handleImageCapture } from 'src/v3/utilities/filesystem'

type NREMTFormProps = {
  nremtLicense?: NREMTLicense
  lsid: string
  userID: string
  open: boolean
  setOpen: (status: boolean) => void
  onError: (error: string) => void
}

export let NREMTLicenseForm = ({
  nremtLicense,
  lsid,
  userID,
  open,
  setOpen,
  onError,
}: NREMTFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let [image, setImage] = useState<FileData | undefined>(undefined)
  let [saving, setSaving] = useState(false)

  let form = useForm({
    defaultValues: {
      id: nremtLicense?.id,
      number: nremtLicense?.nremtidNumber || '',
      issueDate: !nremtLicense?.issueDate
        ? ''
        : dayjs(nremtLicense.issueDate).format('MM/DD/YYYY'),
      expiration: !nremtLicense?.expiration
        ? ''
        : dayjs(nremtLicense.expiration).format('MM/DD/YYYY'),
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let request = !nremtLicense ? axios.post : axios.put
      request('/api/manage/nremt', {
        id: value.id,
        userID: !nremtLicense ? userID : undefined,
        nremtIdNumber: value.number,
        issueDate: value.issueDate,
        expiration: value.expiration,
      })
        .then(pluckData)
        .then((data) =>
          !image
            ? undefined
            : axios
                .put<string>('/api/manage/nremt/image', {
                  id: nremtLicense?.id || data.id,
                  imageType:
                    image.extension === 'jpeg' || image.extension === 'jpg'
                      ? 'jpg'
                      : image.extension === 'png'
                        ? 'png'
                        : 'pdf',
                })
                .then(pluckData)
                .then((response) =>
                  axios.put(response, image?.file, {
                    headers: {
                      'Cache-Control': 'max-age=2592000',
                      'content-type': image?.type,
                    },
                  }),
                ),
        )
        .then(() => dispatch(fetchNREMTLicense(userID)))
        .then(() =>
          dispatch(
            fetchNREMTVerificationStatus({
              linkedServiceID: lsid,
              userID,
            }),
          ),
        )
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          onError?.(pluckError(err, 'Error Updating NREMT License'))
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 300, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          {!nremtLicense ? 'Add' : 'Edit'} NREMT License
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <form.Field
              name="number"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) return 'NREMT Number is a required field.'
                  return ['R', 'E', 'A', 'M'].some(
                    (prefix) => value[0].toUpperCase() === prefix,
                  )
                    ? undefined
                    : 'Invalid NREMT Number: Your ID must start with an allowed prefix (R, E, A, or M)'
                },
              }}
              children={(field) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: 'background.paper' }}
                  value={field.state.value}
                  label="NREMT Number"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
            <form.Field
              name="issueDate"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) return 'Issue Date is a required field'
                  if (!dayjs(value, 'MM/DD/YYYY', true).isValid())
                    return 'Invalid date'
                  return undefined
                },
              }}
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Issue Date"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        sx: { bgcolor: 'background.paper' },
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="expiration"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) return 'Expiration is a required field'
                  if (!dayjs(value, 'MM/DD/YYYY', true).isValid())
                    return 'Invalid date'
                  return undefined
                },
              }}
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        sx: { bgcolor: 'background.paper' },
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <Box>
              <label style={{ marginTop: 0 }}>
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  <input
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={(e) => handleImageCapture(e, setImage, onError)}
                  />
                  {!nremtLicense || !nremtLicense?.imageName
                    ? 'Select'
                    : 'Overwrite'}{' '}
                  File
                </Button>
              </label>
              {image && (
                <Typography variant="subtitle1">
                  <UploadFileIcon
                    color="primary"
                    sx={{ mr: '2px', mb: '2px' }}
                  />
                  {image.name}
                </Typography>
              )}
              {nremtLicense?.imageName && (
                <Typography color="text.secondary" variant="subtitle2">
                  *The existing NREMT file will only be overwritten upon
                  submitting this form with a new image uploaded.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              onClick={handleOnClose}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
